"use client";

import { useState } from "react";

import { api, humanMessage } from "../api";
import { useSession } from "../session";
import type { Coupon, CouponPin, CouponStatus, Paged } from "../types";
import { useAsync, type Async } from "../useAsync";

/*
 * 내 쿠폰 — 🔴 **회원만**. 로그인 확인이 끝나기 전에는 부르지 않습니다(손님의 401 이
 *    로그인 화면 이동을 일으키지 않게). 손님을 로그인 화면으로 보내는 것은 화면의
 *    `useRequireMember()` 몫입니다.
 *
 * 사용:
 *   const list = useCoupons("issued", page);            // list.data: Paged<Coupon> | null
 *   const counts = useCouponCounts(["issued", "used"]); // counts.data: { issued: 3, used: 1 } | null
 *   const coupon = useCoupon(id);                        // coupon.data: Coupon | null
 *   const pin = useCouponPin(id);                        // pin.reveal() → pin.pin?.pinNo · pin.copy()
 */
export function useCoupons(status: CouponStatus | string, page = 1, options: { perPage?: number; enabled?: boolean } = {}): Async<Paged<Coupon>> {
  const session = useSession();
  return useAsync(
    (signal) => api.get<Paged<Coupon>>("/coupons", { query: { status, page, per_page: options.perPage }, signal }),
    `coupons-${status}-${page}-${options.perPage ?? ""}`,
    session.status === "member" && options.enabled !== false,
  );
}

/**
 * 탭 옆 개수. 🔑 `per_page: 1` 로 부르고 `total` 만 씁니다 — 목록 자체를 탭 수만큼
 *    받을 이유가 없습니다.
 */
export function useCouponCounts(statuses: readonly string[]): Async<Record<string, number>> {
  const session = useSession();
  return useAsync(
    async (signal) => {
      const results = await Promise.all(
        statuses.map((status) => api.get<Paged<Coupon>>("/coupons", { query: { status, page: 1, per_page: 1 }, signal })),
      );
      const map: Record<string, number> = {};
      statuses.forEach((status, index) => {
        map[status] = results[index].total;
      });
      return map;
    },
    `coupon-counts-${statuses.join(",")}`,
    session.status === "member",
  );
}

export function useCoupon(id: string): Async<Coupon> {
  const session = useSession();
  return useAsync(
    (signal) => api.get<Coupon>(`/coupons/${encodeURIComponent(id)}`, { signal }),
    `coupon-${id}`,
    session.status === "member",
  );
}

/**
 * 핀번호 — 🔴 **들어오자마자 부르지 않습니다.** `reveal()` 을 눌러야 `GET /coupons/{id}/pin` 이
 *    나갑니다 (목록 응답에는 핀이 아예 없습니다 — 옆 사람에게 통째로 보이지 않게).
 *
 * 🔴 `copy()` 는 실패를 삼키지 않습니다 — 하이브리드 웹뷰는 클립보드가 막혀 있는 경우가
 *    있고, 조용히 실패하면 사용자는 복사된 줄 알고 붙여 넣습니다. 결과 문구가 `copied` 입니다.
 */
export function useCouponPin(id: string) {
  const [pin, setPin] = useState<CouponPin | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState("");

  async function reveal() {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      setPin(await api.get<CouponPin>(`/coupons/${encodeURIComponent(id)}/pin`));
    } catch (cause) {
      setError(humanMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  async function copy() {
    if (!pin) return;
    try {
      await navigator.clipboard.writeText(pin.pinNo);
      setCopied("핀번호를 복사했습니다.");
    } catch {
      setCopied("이 화면에서는 복사할 수 없습니다. 위 번호를 그대로 적어 주세요.");
    }
  }

  return { pin, busy, error, reveal, copy, copied };
}

/**
 * 네 자리씩 끊어 읽기 쉽게. 🔑 화면에만 공백을 넣습니다 — 복사는 원본(`pin.pinNo`)입니다.
 */
export function groupDigits(pin: string): string {
  const clean = pin.replace(/\s+/g, "");
  if (clean.length <= 4) return clean;
  return clean.replace(/(.{4})/g, "$1 ").trim();
}
